import {
  User,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { firebaseAuth, firestoreDB } from "../firebase/firebase-config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { AccountType, Id, RolesForEvent, UserDetails } from "../interfaces/types";
import {
  fetchUsers,
  updateUserDetailsInFirebase,
} from "../services/firebaseService";

// Define the type for the context, including user, user details, loading state, verification state, and authentication functions
export type UserContextType = {
  user?: User;
  userDetails?: UserDetails;
  isLoading: boolean;
  isVerified: boolean;
  signup: (
    email: string,
    password: string,
    username: string,
    firstName: string,
    lastName: string
  ) => Promise<{ user?: User; error?: string }>;
  signin: (
    email: string,
    password: string
  ) => Promise<{ user?: User; error?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  addEventRoleToCurrentUser: (eventId: Id, role: RolesForEvent) => void;
  updateEventRoleOnUsers: (
    eventId: Id,
    role: RolesForEvent,
    prevUsersThatHadTheRole: UserDetails[],
    usersToGetTheRole: UserDetails[]
  ) => void;
  getUserById: (userId: Id | undefined) => UserDetails | undefined;
  getEventManagers: (eventId: Id) => UserDetails[];
  getPossibleEventManagers: () => UserDetails[];
};

// Create the context with default values
const UserContext = createContext<UserContextType>({
  signup: async () => ({}),
  signin: async () => ({}),
  logout: async () => {},
  isLoading: true,
  isVerified: false,
  resetPassword: async () => ({}),
  addEventRoleToCurrentUser: () => {},
  updateEventRoleOnUsers: () => {},
  getUserById: () => undefined,
  getEventManagers: () => [],
  getPossibleEventManagers: () => [],
});

// Define the UserProvider component which will wrap the children components and provide the context
export const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [userDetails, setUserDetails] = useState<UserDetails | undefined>(
    undefined
  );
  const [allUsers, setAllUsers] = useState<UserDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    fetchUsers().then((fetchedUsers) => {
      if (fetchedUsers) {
        setAllUsers(fetchedUsers);
      }
    });
  }, []);

  // Monitor authentication state changes
  useEffect(() => {
    // Set up the onAuthStateChanged listener
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user: any) => {
      setIsLoading(false);
      if (user) {
        setUser(user);
        const userDocRef = doc(firestoreDB, "users", user.uid);
        // Set up the onSnapshot listener
        const unsubscribeSnapshot = onSnapshot(
          userDocRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              setUserDetails(docSnapshot.data() as UserDetails);
            }
          },
          (error) => {
            if (error.code === "permission-denied") {
              // Handle permission denied error
              setUser(undefined);
              setUserDetails(undefined);
            }
          }
        );

        setIsVerified(user.emailVerified);

        // Unsubscribe from the onSnapshot listener when the user changes
        return () => unsubscribeSnapshot();
      } else {
        setUser(undefined);
        setUserDetails(undefined);
        setIsVerified(false);
      }
    });

    // Unsubscribe from the onAuthStateChanged listener when the component unmounts
    return () => unsubscribe();
  }, []);

  //persist user details to firebase after every change
  useEffect(() => {
    if (userDetails) {
      updateUserDetailsInFirebase(userDetails);
    }
  }, [userDetails]);

  // Function to sign up a new user
  const signup = async (
    email: string,
    password: string,
    username: string,
    firstName: string,
    lastName: string
  ): Promise<{ user?: User; error?: string }> => {
    try {
      const signInMethods = await fetchSignInMethodsForEmail(
        firebaseAuth,
        email
      );
      if (signInMethods.length > 0) {
        return { error: "Email already exists" };
      }

      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      const newUser = userCredential.user;

      await setDoc(doc(firestoreDB, "users", newUser.uid), {
        uid: newUser.uid,
        email: newUser.email,
        bio: "",
        username,
        firstName,
        lastName,
        profileImageUrl: "",
        professions: [],
        roles: ["user"], // Default role (old)
        createdAt: new Date(),
        eventRoles: [],
        accountType: AccountType.COMMUNITY, // Default account type
      });

      await sendEmailVerification(newUser); // Send verification email

      setUser(newUser);
      setIsVerified(newUser.emailVerified);

      return { user: newUser };
    } catch (error) {
      console.error(error);
      return { error: (error as { message: string; code: string }).message };
    }
  };

  // Function to sign in an existing user
  const signin = async (
    email: string,
    password: string
  ): Promise<{ user?: User; error?: string }> => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      setUser(userCredential.user);
      setIsVerified(userCredential.user.emailVerified);
      return { user: userCredential.user };
    } catch (error) {
      const errorCode = (error as { code: string }).code;
      if (
        errorCode === "auth/user-not-found" ||
        errorCode === "auth/wrong-password" ||
        errorCode === "Firebase: Error (auth/invalid-credential)" ||
        errorCode === "400" ||
        errorCode.includes("Firebase")
      ) {
        return { error: "Email not found or password incorrect" };
      }
      return { error: (error as { message: string; code: string }).message };
    }
  };

  // Function to sign out the current user
  const logout = async () => {
    setUser(undefined);
    setUserDetails(undefined);
    await firebaseAuth.signOut();
  };

  // Function to reset the password for a given email
  const resetPassword = async (email: string): Promise<{ error?: string }> => {
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
      return {};
    } catch (error) {
      console.error(error);
      return { error: (error as { message: string; code: string }).message };
    }
  };

  // Function to add an event role to the current user
  const addEventRoleToCurrentUser = (eventId: Id, role: RolesForEvent) => {
    if (!userDetails) return;
    if (
      userDetails.eventRoles
        .find((er) => er.eventId === eventId.toString())
        ?.roles?.includes(role)
    )
      return;
    const prevRoles = userDetails?.eventRoles?.find(
      (er) => er.eventId === eventId.toString()
    )?.roles;
    if (userDetails) {
      setUserDetails({
        ...userDetails,
        eventRoles: [
          ...(userDetails.eventRoles.filter((er) => er.eventId !== eventId) ||
            []),
          { eventId: eventId.toString(), roles: [...(prevRoles || []), role] },
        ],
      });
    }
  };

  //adds the role to the users that are supposed to have it and removes it from the users that are not supposed to have it
  const updateEventRoleOnUsers = (
    eventId: Id,
    role: RolesForEvent,
    usersThatHadTheRole: UserDetails[],
    usersToGetTheRole: UserDetails[]
  ) => {
    if (!allUsers) return;
    usersToGetTheRole.forEach((user) => {
      //if user already has the role, return
      if (usersThatHadTheRole.includes(user)) return;
      else {
        const prevRoles = user?.eventRoles?.find(
          (er) => er.eventId === eventId.toString()
        )?.roles;
        if (prevRoles?.includes(role)) return;
        else {
          const newUserDetails = {
            ...user,
            eventRoles: [
              ...(user.eventRoles.filter((er) => er.eventId !== eventId) || []),
              {
                eventId: eventId.toString(),
                roles: [...(prevRoles || []), role],
              },
            ],
          };
          updateUserDetailsInFirebase(newUserDetails);
          setAllUsers((prevUsers) => {
            return prevUsers.map((u) => {
              if (u.uid === newUserDetails.uid) return newUserDetails;
              else return u;
            });
          });
        }
      }
    });

    usersThatHadTheRole.forEach((user) => {
      //if user can remain the role, return
      if ( usersToGetTheRole.includes(user)) return;
      else {
        const prevRoles = user?.eventRoles?.find(
          (er) => er.eventId === eventId.toString()
        )?.roles;
        if (prevRoles?.includes(role)) {
          const newUserDetails = {
            ...user,
            eventRoles: [
              ...(user.eventRoles.filter((er) => er.eventId !== eventId) || []),
              {
                eventId: eventId.toString(),
                roles: prevRoles?.filter((r) => r !== role),
              },
            ],
          };
          updateUserDetailsInFirebase(newUserDetails);
          setAllUsers((prevUsers) => {
            return prevUsers.map((u) => {
              if (u.uid === newUserDetails.uid) return newUserDetails;
              else return u;
            });
          });
        }
      }
    });
  };

  const getUserById = (userId: Id | undefined) => {
    if (!userId) return;
    return allUsers.find((user) => user.uid === userId);
  };

  const getEventManagers = (eventId: Id): UserDetails[] => {
    if (!allUsers) return [];
    const eventManagers = allUsers.filter(
      (user) =>
        user.eventRoles.find((er) => er.eventId === eventId.toString())
          ?.roles?.includes(RolesForEvent.MANAGER)
    );
    return eventManagers || [];
  }

  const getPossibleEventManagers = (): UserDetails[] => {
    if (!allUsers) return [];
    const possibleEventManagers = allUsers.filter(
      (user) => [AccountType.ADMIN, AccountType.VOLUNTEER].includes(user.accountType)
    );
    return possibleEventManagers || [];
  }

  const value: UserContextType = {
    user,
    userDetails,
    isLoading,
    isVerified,
    signup,
    signin,
    logout,
    resetPassword,
    addEventRoleToCurrentUser,
    updateEventRoleOnUsers,
    getUserById,
    getEventManagers,
    getPossibleEventManagers,

  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  return useContext(UserContext);
};
